import React, { useEffect, useState, useCallback } from 'react';
import './PersonalWidget.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import EmployeeService from '@frontend/AppSettings/services/EmployeeService';
import useActiveEmployeeId from '@frontend/AppSettings/hooks/useActiveEmployeeId';
import { EmployeeGetResponse } from '@frontend/Models/Employee/EmployeeRequestResponseModels';

// Extended employee type to include optional fields that may come from the API
type ExtendedEmployee = EmployeeGetResponse & {
  title?: string;
  age?: string | number;
  city?: string;
};

const PersonalWidget: React.FC = () => {
  const employeeId = useActiveEmployeeId();
  const [employee, setEmployee] = useState<ExtendedEmployee>({} as ExtendedEmployee);
  const [editMode, setEditMode] = useState<boolean>(false);
  const [newBilledRate, setNewBilledRate] = useState<string>('');

  const fetchEmployee = useCallback(async () => {
    if (!employeeId) {
      setEmployee({} as ExtendedEmployee);
      return;
    }

    try {
      const employeeData = await EmployeeService.getEmployeeById(employeeId);
      setEmployee(employeeData as ExtendedEmployee);
    } catch (error) {
      console.error('Error fetching employee data:', error);
    }
  }, [employeeId]);

  useEffect(() => {
    fetchEmployee();
  }, [fetchEmployee]);

  const handleUpdateEmployee = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!employee.employeeid) return;

    try {
      const billedRateNumber = newBilledRate ? parseFloat(newBilledRate) : null;
      await EmployeeService.updateEmployee(employee.employeeid, {
        billedrate: billedRateNumber,
      });

      setEmployee({ ...employee, billedrate: billedRateNumber });
      setEditMode(false);
      setNewBilledRate('');
    } catch (error) {
      console.error('Error updating billed rate:', error);
    }
  };

  const getInitials = (): string => {
    const first = employee.firstname?.[0] || '';
    const last = employee.lastname?.[0] || '';
    return (first + last).toUpperCase() || 'U';
  };

  return (
    <div className="pw-personal-widget">
      <div className="pw-personal-card">
        <div className="pw-profile-avatar">
          <div className="pw-avatar-placeholder">{getInitials()}</div>
        </div>
        <h3 className="pw-profile-name">{`${employee.firstname || ''} ${
          employee.lastname || ''
        }`}</h3>
        <div className="pw-profile-details">
          <div className="pw-profile-info-item">
            <strong>Title:</strong> {employee.title || ''}
          </div>
          <div className="pw-profile-info-item">
            <strong>Age:</strong> {employee.age || ''}
          </div>
          <div className="pw-profile-info-item">
            <strong>Email:</strong> {employee.email || ''}
          </div>
          <div className="pw-profile-info-item">
            <strong>Phone:</strong> {employee.phone || ''}
          </div>
          <div className="pw-profile-info-item">
            <strong>City:</strong> {employee.city || ''}
          </div>
        </div>
        {editMode ? (
          <form className="pw-edit-form" onSubmit={handleUpdateEmployee}>
            <label htmlFor="newBilledRate">Billed Rate ($/hr): </label>
            <input
              type="number"
              id="newBilledRate"
              value={newBilledRate}
              placeholder={employee.billedrate?.toString() || ''}
              onChange={(e) => setNewBilledRate(e.target.value)}
              required
              min="0"
              step="0.01"
            />
            <div className="pw-edit-buttons">
              <button type="submit">Save</button>
              <button type="button" onClick={() => setEditMode(false)}>
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="pw-billed-rate">
            <strong>Billed Rate ($/hr): {employee.billedrate ?? 'N/A'}</strong>
            <button
              className="pw-edit-button"
              onClick={() => {
                setEditMode(true);
                setNewBilledRate(employee.billedrate?.toString() || '');
              }}
              type="button"
              aria-label="Edit billed rate"
            >
              <FontAwesomeIcon icon={faPenToSquare} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalWidget;
